import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Hit, Source } from '@vigie/shared';
import { api } from '../api.js';
import { Card, Empty, Notice, Score, StatusBadge } from '../components.js';
import { formatDay, relative, useAction, useAsync } from '../hooks.js';

type SortKey = 'score' | 'postedAt' | 'discoveredAt' | 'title' | 'status';

const STATUSES = [
  { value: '', label: 'Tous statuts' },
  { value: 'new', label: 'Nouveaux' },
  { value: 'relevant', label: 'Pertinents' },
  { value: 'drafted', label: 'Brouillon prêt' },
  { value: 'posted', label: 'Publiés' },
  { value: 'ignored', label: 'Ignorés' },
];

/** Tri par défaut de chaque colonne : dates et score du plus fort au plus faible, texte de A à Z. */
const DEFAULT_ORDER: Record<SortKey, 'asc' | 'desc'> = {
  score: 'desc',
  postedAt: 'desc',
  discoveredAt: 'desc',
  title: 'asc',
  status: 'asc',
};

function SortableHeader({
  column,
  label,
  sort,
  order,
  onSort,
  className,
  style,
}: {
  column: SortKey;
  label: string;
  sort: SortKey;
  order: 'asc' | 'desc';
  onSort: (column: SortKey) => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const active = sort === column;
  return (
    <th className={className} style={{ ...style, cursor: 'pointer', userSelect: 'none' }}>
      <button
        type="button"
        className="ghost small"
        onClick={() => onSort(column)}
        style={{ padding: 0, font: 'inherit', color: active ? 'var(--accent)' : 'inherit' }}
        aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {label}
        <span aria-hidden style={{ opacity: active ? 1 : 0.25 }}>
          {active ? (order === 'asc' ? '▲' : '▼') : '▾'}
        </span>
      </button>
    </th>
  );
}

export function Inbox({ onChanged }: { onChanged: () => void }) {
  const navigate = useNavigate();

  const [sort, setSort] = useState<SortKey>('score');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [status, setStatus] = useState('');
  const [siteId, setSiteId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [minScore, setMinScore] = useState('');
  const [search, setSearch] = useState('');
  const [postedSince, setPostedSince] = useState('');
  const [postedUntil, setPostedUntil] = useState('');
  const [purpose, setPurpose] = useState<'prospect' | 'intel' | 'all'>('prospect');

  const sites = useAsync(() => api.sites(), []);
  // Toutes les sources de l'utilisateur, pour filtrer sans passer par la config.
  const sources = useAsync(async () => {
    const all = await api.sites();
    const nested = await Promise.all(
      all.map(async (site) => {
        const platforms = await api.platforms(site.id);
        const lists = await Promise.all(
          platforms.map(async (platform) =>
            (await api.sources(platform.id)).map((source) => ({
              source,
              label: `${platform.kind} · ${source.value}`,
            })),
          ),
        );
        return lists.flat();
      }),
    );
    return nested.flat() as { source: Source; label: string }[];
  }, []);

  const hits = useAsync(
    () =>
      api.hits({
        sort,
        order,
        purpose,
        status,
        siteId,
        sourceId,
        q: search || undefined,
        minScore: minScore ? Number(minScore) : undefined,
        postedSince: postedSince ? new Date(postedSince).toISOString() : undefined,
        // Borne haute inclusive : on prend la fin de la journée choisie.
        postedUntil: postedUntil ? `${postedUntil}T23:59:59.999Z` : undefined,
        limit: 100,
      }),
    [sort, order, purpose, status, siteId, sourceId, search, minScore, postedSince, postedUntil],
  );

  const { run, busy, error } = useAction();

  const onSort = (column: SortKey) => {
    if (column === sort) setOrder(order === 'asc' ? 'desc' : 'asc');
    else {
      setSort(column);
      setOrder(DEFAULT_ORDER[column]);
    }
  };

  const setHitStatus = async (hit: Hit, next: string) => {
    const updated = await run(() => api.updateHitStatus(hit.id, next));
    if (updated) {
      hits.reload();
      onChanged();
    }
  };

  const filtersActive =
    status || siteId || sourceId || minScore || search || postedSince || postedUntil;

  const resetFilters = () => {
    setStatus('');
    setSiteId('');
    setSourceId('');
    setMinScore('');
    setSearch('');
    setPostedSince('');
    setPostedUntil('');
  };

  return (
    <>
      <header className="topbar">
        <h1>Inbox</h1>
        <span className="spacer" />
        <div className="inline">
          <select value={siteId} onChange={(event) => setSiteId(event.target.value)} style={{ width: 170 }}>
            <option value="">Tous les sites</option>
            {(sites.data ?? []).map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
          <select
            value={sourceId}
            onChange={(event) => setSourceId(event.target.value)}
            style={{ width: 220 }}
          >
            <option value="">Toutes les sources</option>
            {(sources.data ?? []).map((entry) => (
              <option key={entry.source.id} value={entry.source.id}>
                {entry.label}
              </option>
            ))}
          </select>
          <select
            value={purpose}
            onChange={(event) => setPurpose(event.target.value as 'prospect' | 'intel' | 'all')}
            style={{ width: 190 }}
            title="Les sources de veille concurrentielle n’encombrent pas l’inbox de réponse"
          >
            <option value="prospect">Sources de prospection</option>
            <option value="intel">Sources de veille</option>
            <option value="all">Toutes les sources</option>
          </select>
          {filtersActive ? (
            <button type="button" className="small" onClick={resetFilters}>
              Réinitialiser
            </button>
          ) : null}
          <button type="button" className="small" onClick={hits.reload}>
            Rafraîchir
          </button>
        </div>
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}
        {hits.error ? <Notice kind="error">{hits.error}</Notice> : null}

        <Card title={`${hits.data?.hits.length ?? 0} discussion(s)`} tight>
          <table>
            <thead>
              <tr>
                <SortableHeader column="score" label="Score" sort={sort} order={order} onSort={onSort} style={{ width: 96 }} />
                <SortableHeader column="title" label="Discussion" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="status" label="Statut" sort={sort} order={order} onSort={onSort} style={{ width: 132 }} />
                <SortableHeader column="postedAt" label="Publiée" sort={sort} order={order} onSort={onSort} style={{ width: 110 }} />
                <SortableHeader column="discoveredAt" label="Détectée" sort={sort} order={order} onSort={onSort} style={{ width: 105 }} />
                <th style={{ width: 210 }} />
              </tr>
              {/* Ligne de filtres, une commande par colonne. */}
              <tr>
                <th>
                  <select
                    value={minScore}
                    onChange={(event) => setMinScore(event.target.value)}
                    className="small"
                  >
                    <option value="">tous</option>
                    <option value="8">≥ 8</option>
                    <option value="7">≥ 7</option>
                    <option value="6">≥ 6</option>
                    <option value="4">≥ 4</option>
                  </select>
                </th>
                <th>
                  <input
                    value={search}
                    placeholder="rechercher dans le titre…"
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </th>
                <th>
                  <select value={status} onChange={(event) => setStatus(event.target.value)}>
                    {STATUSES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </th>
                <th colSpan={2}>
                  <div className="inline" style={{ flexWrap: 'nowrap' }}>
                    <input
                      type="date"
                      value={postedSince}
                      title="Publiée après le"
                      onChange={(event) => setPostedSince(event.target.value)}
                    />
                    <input
                      type="date"
                      value={postedUntil}
                      title="Publiée avant le"
                      onChange={(event) => setPostedUntil(event.target.value)}
                    />
                  </div>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(hits.data?.hits ?? []).map((hit) => (
                <tr key={hit.id}>
                  <td><Score score={hit.score} /></td>
                  <td>
                    <Link to={`/hits/${hit.id}`}>{hit.title}</Link>
                    {hit.isManual ? <span className="badge" style={{ marginLeft: 6 }}>manuel</span> : null}
                    <div className="small muted">{hit.scoreReason ?? 'Pas encore scoré.'}</div>
                    <a className="small mono" href={hit.url} target="_blank" rel="noreferrer">
                      voir la discussion ↗
                    </a>
                  </td>
                  <td><StatusBadge status={hit.status} /></td>
                  <td className="small">
                    {formatDay(hit.postedAt)}
                    <div className="muted">{new Date(hit.postedAt).getFullYear()}</div>
                  </td>
                  <td className="small muted">{relative(hit.discoveredAt)}</td>
                  <td>
                    <div className="inline">
                      <button
                        type="button"
                        className="small"
                        disabled={busy || hit.status === 'relevant'}
                        onClick={() => setHitStatus(hit, 'relevant')}
                      >
                        Pertinent
                      </button>
                      <button
                        type="button"
                        className="small"
                        disabled={busy || hit.status === 'ignored'}
                        onClick={() => setHitStatus(hit, 'ignored')}
                      >
                        Ignorer
                      </button>
                      <button
                        type="button"
                        className="small primary"
                        onClick={() => navigate(`/hits/${hit.id}`)}
                      >
                        Rédiger
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {hits.data && hits.data.hits.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <Empty>
                      {filtersActive
                        ? 'Aucune discussion ne correspond à ces filtres.'
                        : 'Rien à traiter ici. Le gisement réel est de 10 à 25 fils par an et par site : c’est normal que l’inbox soit souvent vide.'}
                    </Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>

        {hits.data?.nextCursor ? (
          <div className="small muted">
            100 premiers résultats affichés — affinez les filtres pour voir la suite.
          </div>
        ) : null}
      </div>
    </>
  );
}
